/**
 * Frente A / Q8 (spec antecipe-registration §5.1) — "Regra para antecipação" da regra da
 * política de pagamento. Vazio (`null` no backend) = Proporcional com limite; a exceção
 * aprovada continua acima.
 *
 * `ANTECIPE_RULE_DEFAULT` existe porque o `v-autocomplete` trata `null` como "sem seleção":
 * o padrão precisa de um valor próprio na tela, que vira `null` só no payload.
 */
import { ProportionalityRuleEnum } from '@/Network/Types/Requests/ScheduleRequests'
import { proportionalityRuleOptions } from '@/utils/schedule'

export const ANTECIPE_RULE_DEFAULT = 'DEFAULT' as const

export type AntecipeRuleChoice = ProportionalityRuleEnum | typeof ANTECIPE_RULE_DEFAULT

/**
 * Valor que veio do GET da política, guardado como chegou. A presença deste marcador
 * prova que o backend já conhece o campo (mesmo com a flag web desligada).
 */
export interface LoadedAntecipeRule {
  raw: string | null
}

export interface AntecipeRuleFields {
  antecipeRule?: AntecipeRuleChoice
  loadedAntecipeRule?: LoadedAntecipeRule
}

export const antecipeRuleOptions: { title: string; value: AntecipeRuleChoice }[] = [
  { value: ANTECIPE_RULE_DEFAULT, title: 'Padrão (Proporcional com limite)' },
  ...proportionalityRuleOptions.map((option) => ({ value: option.value, title: option.title }))
]

const KNOWN_RULES = Object.values(ProportionalityRuleEnum) as string[]

export function antecipeRuleFromApi(raw: unknown): AntecipeRuleChoice {
  return typeof raw === 'string' && KNOWN_RULES.includes(raw)
    ? (raw as ProportionalityRuleEnum)
    : ANTECIPE_RULE_DEFAULT
}

/**
 * Converte a regra do GET para os campos da store. Só marca `loadedAntecipeRule` quando a
 * chave veio na resposta — backend antigo (sem a chave) deixa a regra sem marcador.
 */
export function antecipeRuleFieldsFromApi(apiRule: {
  antecipeProportionalityRule?: string | null
}): AntecipeRuleFields {
  const antecipeRule = antecipeRuleFromApi(apiRule.antecipeProportionalityRule)
  if (!('antecipeProportionalityRule' in apiRule)) return { antecipeRule }
  return {
    antecipeRule,
    loadedAntecipeRule: { raw: apiRule.antecipeProportionalityRule ?? null }
  }
}

/**
 * O update da política apaga e recria todas as regras e grava `?? null`, então omitir a
 * chave apaga a regra configurada. Por isso:
 * - flag ligada: manda sempre a escolha da tela (padrão vira `null`);
 * - flag desligada com valor carregado do GET: manda o valor carregado, intocado;
 * - flag desligada sem valor carregado (backend antigo ou regra nova): OMITE a chave,
 *   payload idêntico ao de hoje.
 */
export function antecipeRuleForPayload(
  rule: AntecipeRuleFields,
  enabled: boolean
): { antecipeProportionalityRule?: ProportionalityRuleEnum | null } {
  if (!enabled) {
    if (!rule.loadedAntecipeRule) return {}
    return {
      antecipeProportionalityRule: rule.loadedAntecipeRule.raw as ProportionalityRuleEnum | null
    }
  }
  const choice = rule.antecipeRule ?? ANTECIPE_RULE_DEFAULT
  return { antecipeProportionalityRule: choice === ANTECIPE_RULE_DEFAULT ? null : choice }
}
